// @flow

// Vendors
import React, { useState } from 'react';

// Components
import { Form, FormField, FormSubmit, Notice, Modal, CheckboxList } from '..';

// Requests
import { createUserRequest, checkOrganizationKeyRequest } from '../../requests';

// Helpers
import { slugify } from '../../../lib/helpers';

// Data
import {
  getCountriesAsOptions,
  getRegionsAsOptions,
  userAgreement
} from '../../../data';

const countries = getCountriesAsOptions();

const RegistrationForm = () => {
  const { organization, organizationTypes, marketTypes } = window;

  const organizationMarketOptions = marketTypes.map(
    ({ post_title, post_name }) => ({
      label: post_title,
      value: post_name
    })
  );

  const isAdmin = organization && organization.id;
  const [formSuccess, setFormSuccess] = useState('');
  const [checkingOrganizationKey, setCheckingOrganizationKey] = useState(false);
  const [states, setStates] = useState([]);
  const initialValues = {
    firstName: '',
    lastName: '',
    organizationId: (organization || {}).id || '',
    organizationJoin: false,
    organizationName: '',
    organizationType: 'end_user',
    organizationKey: '',
    jobTitle: '',
    country: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    organizationMarketPreferences: [],
    email: '',
    password: '',
    emailPreferencesUpdates: true,
    emailPreferencesMarketing: true,
    acceptUserAgreement: false
  };

  const handleSubmit = ({
    values,
    setValues,
    setValue,
    setErrors,
    setSubmitting
  }) => {
    setSubmitting(true);
    setValue('organizationKey', slugify(values.organizationKey));

    createUserRequest({
      ...values,
      organizationKey: slugify(values.organizationKey)
    })
      .then(response => {
        setFormSuccess(
          `${
            isAdmin ? 'New user added.' : 'Registration successful.'
          }  Redirecting...`
        );
        setTimeout(() => {
          window.location = isAdmin
            ? '/resource-center/users'
            : '/resource-center';
        }, 1000);
      })
      .catch(({ data }) => {
        setSubmitting(false);
        if (data.errors) setErrors(data.errors);
      });
  };

  const handleOrganizationKeyBlur = ({ values, setValue, setError }) => {
    if (!values.organizationKey) return setError('organizationKey', '');

    setValue('organizationKey', slugify(values.organizationKey));
    setCheckingOrganizationKey(true);

    checkOrganizationKeyRequest(slugify(values.organizationKey))
      .then(({ data }) => {
        setCheckingOrganizationKey(false);

        if (!data.organization_exists && values.organizationJoin) {
          return setError(
            'organizationKey',
            `Sorry! We couldn't find an organization matching that key.`
          );
        } else if (data.organization_exists && !values.organizationJoin) {
          return setError(
            'organizationKey',
            'An organization with that key already exists. Please try a different one.'
          );
        }

        return setError('organizationKey', '');
      })
      .catch(({ data }) => setCheckingOrganizationKey(false));
  };

  const handleOrganizationJoinChange = ({
    values,
    setValues,
    errors,
    setErrors
  }) => {
    const fields = {
      organizationName: '',
      organizationKey: '',
      organizationType: '',
      street: '',
      city: '',
      state: '',
      zipCode: '',
      phone: ''
    };

    setValues({
      ...values,
      ...fields,
      organizationJoin: !values.organizationJoin
    });
    setErrors({
      ...errors,
      ...fields
    });
  };

  const handleCountryChange = (event, { values, setValues }) => {
    let country = '';
    let states = [];
    const countryShortCode = event.target.value;

    if (countryShortCode) states = getRegionsAsOptions(countryShortCode);

    setStates(states);
    setValues({
      ...values,
      country: countryShortCode,
      state: states && states.length === 1 ? states[0].value : ''
    });
  };

  const initModalData = {
    modalID: '',
    title: '',
    description: ''
  };

  const [modalData, setModalData] = useState({ ...initModalData });

  const toggleModal = (status, data) => {
    status === true
      ? setModalData({ ...data })
      : setModalData({ ...initModalData });
  };

  const handleClickOpen = (status, data) => toggleModal(status, data);

  return (
    <>
      <Form
        className="account-form card"
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({
          values,
          errors,
          isSubmitting,
          handleChange,
          setSubmitting,
          setError,
          setErrors,
          setValue,
          setValues,
          getValue
        }) => (
          <>
            <div className="card__section">
              <h4>Personal Details</h4>
              <FormField
                name="firstName"
                label="First Name"
                onChange={handleChange}
                value={values.firstName}
                error={errors.firstName}
                disabled={isSubmitting}
              />
              <FormField
                name="lastName"
                label="Last Name"
                onChange={handleChange}
                value={values.lastName}
                error={errors.lastName}
                disabled={isSubmitting}
              />
              <FormField
                name="jobTitle"
                label="Job Title"
                onChange={handleChange}
                value={values.jobTitle}
                error={errors.jobTitle}
                disabled={isSubmitting}
              />
            </div>

            <hr className="u--margin-0" />

            {!isAdmin && (
              <>
                <div className="card__section">
                  <h4>Organization</h4>
                  <FormField
                    name="organizationJoin"
                    label="Would you like to create a new organization or join an existing one?"
                    toggleLabelOn="Join"
                    toggleLabelOff="Create"
                    type="toggle"
                    onChange={handleChange}
                    onChange={() =>
                      handleOrganizationJoinChange({
                        values,
                        setValues,
                        errors,
                        setErrors
                      })
                    }
                    value={values.organizationJoin}
                    error={errors.organizationJoin}
                    disabled={isSubmitting}
                  />

                  {values.organizationJoin && (
                    <FormField
                      name="organizationKey"
                      label="Enter your organization's key"
                      helpText="The organization key must be provided to you by the organization admin."
                      onChange={handleChange}
                      onBlur={() =>
                        handleOrganizationKeyBlur({
                          values,
                          setValue,
                          setError
                        })
                      }
                      value={values.organizationKey}
                      error={errors.organizationKey}
                      disabled={isSubmitting}
                      readOnly={checkingOrganizationKey}
                    />
                  )}

                  {!values.organizationJoin && (
                    <>
                      <FormField
                        name="organizationName"
                        label="Organization Name"
                        onChange={handleChange}
                        value={values.organizationName}
                        error={errors.organizationName}
                        disabled={isSubmitting}
                      />
                      <FormField
                        name="organizationType"
                        label="Organization Type"
                        type="select"
                        options={organizationTypes}
                        onChange={handleChange}
                        value={values.organizationType}
                        error={errors.organizationType}
                        disabled={isSubmitting}
                      />
                      <FormField
                        name="country"
                        label="Country"
                        type="select"
                        options={countries}
                        emptyOptionLabel="Select a country..."
                        onChange={event =>
                          handleCountryChange(event, { values, setValues })
                        }
                        value={values.country}
                        error={errors.country}
                        disabled={isSubmitting}
                      />
                      <FormField
                        name="street"
                        label="Street Address"
                        onChange={handleChange}
                        value={values.street}
                        error={errors.street}
                        disabled={isSubmitting}
                      />
                      <FormField
                        name="city"
                        label="City"
                        onChange={handleChange}
                        value={values.city}
                        error={errors.city}
                        disabled={isSubmitting}
                      />
                      <FormField
                        name="state"
                        label="State"
                        type="select"
                        options={states}
                        emptyOptionLabel="Select a state..."
                        optionMapKey="label"
                        onChange={handleChange}
                        value={values.state}
                        error={errors.state}
                        disabled={isSubmitting || states.length < 2}
                      />
                      <FormField
                        type="zipCode"
                        onChange={handleChange}
                        value={values.zipCode}
                        error={errors.zipCode}
                        disabled={isSubmitting}
                      />
                      <FormField
                        type="phone"
                        onChange={handleChange}
                        value={values.phone}
                        error={errors.phone}
                        disabled={isSubmitting}
                      />

                      <hr />

                      <h4 className="u--margin-bottom-0">
                        Organization Market Interests
                      </h4>
                      <h6>Must Select one</h6>
                      <CheckboxList
                        options={organizationMarketOptions}
                        parentFieldName="organizationMarketPreferences"
                        onChange={handleChange}
                        values={values}
                        error={errors.organizationMarketPreferences}
                        setValue={setValue}
                        disabled={isSubmitting}
                      />
                    </>
                  )}
                </div>
                <hr className="u--margin-0" />
              </>
            )}

            <div className="card__section">
              <h4>Email Preferences</h4>

              <FormField
                name="emailPreferencesUpdates"
                label="I would like to receive notifications when documents are added or updated within the Telecor Resource Center."
                type="emailPreferencesUpdates"
                type="checkbox"
                onChange={handleChange}
                value={values.emailPreferencesUpdates}
                error={errors.emailPreferencesUpdates}
                disabled={isSubmitting}
              />
              <FormField
                name="emailPreferencesMarketing"
                label="I would like to receive marketing-related materials from Telecor."
                type="emailPreferencesMarketing"
                type="checkbox"
                onChange={handleChange}
                value={values.emailPreferencesMarketing}
                error={errors.emailPreferencesMarketing}
                disabled={isSubmitting}
              />
            </div>

            <hr className="u--margin-0" />

            <div className="card__section">
              <h4>Account Details</h4>

              <FormField
                name="email"
                label="Email"
                type="email"
                onChange={handleChange}
                value={values.email}
                error={errors.email}
                disabled={isSubmitting}
              />
              {!isAdmin && (
                <FormField
                  name="password"
                  label="Password"
                  type="password"
                  onChange={handleChange}
                  value={values.password}
                  error={errors.password}
                  disabled={isSubmitting}
                />
              )}
              {isAdmin && (
                <p>
                  <strong>Password</strong>
                  <br />
                  <em>
                    An email will be sent to the new user prompting them to set
                    up their password.
                  </em>
                </p>
              )}
              <FormField
                name="acceptUserAgreement"
                label={
                  <>
                    I accept{' '}
                    <button
                      type="button"
                      className="btn btn--link u--padding-0 u--border-width-0"
                      onClick={() => handleClickOpen(true, userAgreement)}
                    >
                      Telecor's User Agreement
                    </button>
                  </>
                }
                type="checkbox"
                onChange={handleChange}
                checked={values.acceptUserAgreement}
                error={errors.acceptUserAgreement}
                disabled={isSubmitting}
              />
            </div>

            <hr className="u--margin-0" />

            <div className="card__section">
              <FormSubmit
                loading={isSubmitting}
                disabled={isSubmitting}
                disabledLabel={
                  isSubmitting && !formSuccess ? 'Submitting...' : 'Submit'
                }
              />
              {errors.formError && (
                <Notice
                  type="danger"
                  message={errors.formError}
                  dismiss={() => setError('formError', '')}
                />
              )}
              {formSuccess && <Notice type="success" message={formSuccess} />}
            </div>
          </>
        )}
      />
      <Modal {...modalData} toggleModal={toggleModal} size="modal--md" />
    </>
  );
};

export default RegistrationForm;
